/** Screen-space overlay models, projected from world coordinates each frame. */

import type { RunwayDefinition, Vec2 } from "../airport/AirportDefinition";
import { FEEDBACK } from "../config/gameplay";
import { colorToCss, PALETTE } from "../config/palette";
import { RUNWAY_SYMBOLS } from "../config/symbols";
import type { RunwayStatus } from "../game/snapshot";
import type { SpawnWarning } from "../simulation/SpawnSystem";

export interface ScreenBadge {
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly label: string;
  /** Colour-blind shape for the runway; empty when the mode is off. */
  readonly symbol: string;
}

export interface ScreenHint {
  /** Bubble position; clamped to stay on screen. */
  readonly x: number;
  readonly y: number;
  /** The thing the bubble is about, unclamped. */
  readonly anchorX: number;
  readonly anchorY: number;
  readonly text: string;
}

export interface TrafficBadge {
  readonly x: number;
  readonly y: number;
  readonly count: number;
}

/** An arrival that has been released but is not in the world yet. */
export interface SpawnTelegraph {
  readonly key: number;
  readonly x: number;
  readonly y: number;
  /** Where the arrow points, in screen-space degrees. */
  readonly angle: number;
  /** Whole seconds until it appears. */
  readonly seconds: number;
  readonly color: string;
  /** Colour-blind shape; empty when the mode is off. */
  readonly symbol: string;
  /** 0 when the warning was raised, 1 as the aircraft arrives. */
  readonly progress: number;
}

/** The canvas, plus whatever knows how to project world points onto it. */
export interface OverlayViewport {
  readonly width: number;
  readonly height: number;
  readonly project: (point: Vec2) => { x: number; y: number };
}

const BADGE_MARGIN = 34;
const TRAFFIC_MARGIN = 26;
const HINT_MARGIN = 92;
/** How far the badge sits along and to the side of the runway axis. */
const BADGE_ALONG_AXIS = 0.12;
const BADGE_OFF_AXIS = 5.4;

function clamp(value: number, margin: number, extent: number): number {
  return Math.max(margin, Math.min(extent - margin, value));
}

/**
 * Countdown chips for every runway with a departure booked.
 *
 * Anchored just past the threshold and pushed sideways so the chip never sits
 * on the runway itself, then clamped so it survives a rotated camera.
 */
export function runwayBadges(
  statuses: readonly RunwayStatus[],
  runways: readonly RunwayDefinition[],
  viewport: OverlayViewport,
  showSymbols = false,
): ScreenBadge[] {
  const badges: ScreenBadge[] = [];

  for (const status of statuses) {
    if (status.nextDepartureIn === null) {
      continue;
    }
    const runway = runways.find((item) => item.id === status.runwayId);
    if (!runway) {
      continue;
    }

    const axisX = runway.end.x - runway.approach.x;
    const axisY = runway.end.y - runway.approach.y;
    const length = Math.hypot(axisX, axisY) || 1;
    const anchor = viewport.project({
      x:
        runway.approach.x +
        axisX * BADGE_ALONG_AXIS -
        (axisY / length) * BADGE_OFF_AXIS,
      y:
        runway.approach.y +
        axisY * BADGE_ALONG_AXIS +
        (axisX / length) * BADGE_OFF_AXIS,
    });

    const seconds = Math.ceil(status.nextDepartureIn);
    badges.push({
      key: status.runwayId,
      x: clamp(anchor.x, BADGE_MARGIN, viewport.width),
      y: clamp(anchor.y, BADGE_MARGIN, viewport.height),
      color: colorToCss(PALETTE.runway[status.color].marker),
      symbol: showSymbols ? RUNWAY_SYMBOLS[status.color].glyph : "",
      label:
        status.departureQueue > 1
          ? `${seconds}s ·${status.departureQueue}`
          : `${seconds}s`,
    });
  }

  return badges;
}

/** How many aircraft are parked on the apron, pinned near the apron itself. */
export function trafficBadge(
  anchor: Vec2,
  count: number,
  viewport: OverlayViewport,
): TrafficBadge {
  const point = viewport.project(anchor);
  return {
    x: clamp(point.x, TRAFFIC_MARGIN, viewport.width),
    y: clamp(point.y, TRAFFIC_MARGIN, viewport.height),
    count,
  };
}

/**
 * Edge chips for the arrivals the director has already released.
 *
 * The arrow is aimed by projecting where the aircraft is now and where it will
 * cross into view, then taking the screen-space delta — so it keeps pointing
 * the right way through an orbit or a zoom without knowing anything about the
 * camera. The chip itself is clamped to the margin: it is a "look here" for a
 * thing that is by definition off screen.
 */
export function spawnTelegraphs(
  warnings: readonly SpawnWarning[],
  viewport: OverlayViewport,
  showSymbols = false,
): SpawnTelegraph[] {
  const telegraphs: SpawnTelegraph[] = [];

  for (const warning of warnings) {
    const from = viewport.project(warning.pos);
    const entry = viewport.project(warning.entry);
    const dx = entry.x - from.x;
    const dy = entry.y - from.y;

    telegraphs.push({
      key: warning.id,
      x: clamp(entry.x, FEEDBACK.telegraphMargin, viewport.width),
      y: clamp(entry.y, FEEDBACK.telegraphMargin, viewport.height),
      // Degrees, because the chip is rotated by a CSS transform.
      angle:
        Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6
          ? 0
          : (Math.atan2(dy, dx) * 180) / Math.PI,
      seconds: Math.max(0, Math.ceil(warning.warningRemaining)),
      color: colorToCss(PALETTE.runway[warning.kind].marker),
      symbol: showSymbols ? RUNWAY_SYMBOLS[warning.kind].glyph : "",
      progress:
        warning.warningTotal > 0
          ? Math.min(
              1,
              Math.max(0, 1 - warning.warningRemaining / warning.warningTotal),
            )
          : 1,
    });
  }

  return telegraphs;
}

/**
 * Bubble pointing at an aircraft with no route yet.
 *
 * Returns null when the aircraft is off screen — a bubble clamped to the edge
 * would point at nothing.
 */
export function waitingHint(
  position: Vec2 | null,
  text: string,
  viewport: OverlayViewport,
): ScreenHint | null {
  if (!position) {
    return null;
  }

  const point = viewport.project(position);
  const onScreen =
    point.x >= 0 &&
    point.x <= viewport.width &&
    point.y >= 0 &&
    point.y <= viewport.height;
  if (!onScreen) {
    return null;
  }

  return {
    x: clamp(point.x, HINT_MARGIN, viewport.width),
    y: Math.max(44, point.y - 56),
    anchorX: point.x,
    anchorY: point.y,
    text,
  };
}
